import { IkeToken, SubFormType } from '@ikegps/core-api';
import { FormModelFieldSelectList, SpanTypeOptionSet } from '@ikegps/core-shared';
import { ImageButton } from '../../../components/button/image.button';
import { MaterialIcon } from '../../../components/icon/icons';
import { Input } from '../../../components/input/input';
import { InputState, InputStateTypes } from '../../../components/input/input.state';
import { addStyles, DefaultTheme } from '../../../components/theme';
import { T, Translate } from '../../../i18n';
import type { MithrilVNode } from '../../../mithril';
import { m } from '../../../mithril';
import { Services } from '../../../services';
import { classNames } from '../../../util/classnames';
import { FormFieldEditor } from './form.field.editor';

export const CSS = {
	container: 'c-FdcSelectList',
	title: 'c-FdcSelectList__title',
	list: 'c-FdcSelectList__list',
	option: 'c-FdcSelectList__option',
	value: 'c-FdcSelectList__value',
	ghost: 'c-FdcSelectList__ghost',
	color: 'c-FdcSelectList__color',
	colorTitle: 'c-FdcSelectList__colorTitle',
};

const inputHeight = 32;

const colorPickerHeight = 24;
const colorPickerWidth = 32;
const colorPickerMargin = 20;
const keyInputWidth = 320;

addStyles({
	[CSS.container]: {
		width: '100%',
		display: 'flex',
		'flex-direction': 'column',
	},
	[CSS.list]: {
		display: 'flex',
		width: '100%',
		'align-items': 'center',
		padding: DefaultTheme.space(2),
	},
	[CSS.option]: {
		width: `${keyInputWidth}px`,
		height: `${inputHeight}px`,

		display: 'flex',
		alignItems: 'center',

		boxSizing: 'border-box',
		paddingRight: '12px',
	},
	[CSS.ghost]: {
		color: DefaultTheme.font.color.supporting,
	},
	[CSS.value]: {
		width: `${keyInputWidth}px`, // TODO this is from the input width
		height: `${inputHeight}px`,

		display: 'flex',
		alignItems: 'center',

		boxSizing: 'border-box',
		paddingRight: '12px',
	},
	[CSS.colorTitle]: {
		width: `${colorPickerWidth}px`,
		marginRight: `${colorPickerMargin}px`,

		[`& + .${CSS.option}`]: {
			width: `${keyInputWidth - colorPickerWidth - colorPickerMargin}px`,
		},
	},
	[CSS.title]: {
		'font-weight': 'bold',
		'margin-bottom': DefaultTheme.space(1),
		color: DefaultTheme.font.color.supporting,
		height: '20px',
	},
	[CSS.color]: {
		width: `${colorPickerWidth}px`,
		height: `${colorPickerHeight}px`,
		overflow: 'hidden',
		boxSizing: 'border-box',

		borderRadius: '4px',

		border: '2px solid #c9c9c9',

		marginRight: `${colorPickerMargin}px`,

		['& input[type=color]']: {
			width: 'calc(100% + 12px)',
			height: 'calc(100% + 12px)',

			padding: 0,
			margin: '-6px',

			cursor: 'pointer',
		},

		[`& + .${CSS.option}`]: {
			width: `${keyInputWidth - colorPickerWidth - colorPickerMargin}px`,

			[`& .mdl-textfield`]: {
				width: `calc(300px - ${colorPickerWidth + colorPickerMargin}px)`,
			},
		},
	},
});

function setterListValue(field: FormModelFieldSelectList, type: 'text' | 'value' | 'color', index: number) {
	return function (val: string) {
		let { text, value, color } =
			index < field.options.length ? field.options[index] : { text: '', value: String(index), color: '#a0aec0' };
		if (type === 'text') {
			field.Mutator.option({ text: val, value, color }, index);
		} else if (type === 'value') {
			field.Mutator.option({ text, value: val, color }, index);
		} else if (type === 'color') {
			field.Mutator.option({ text, value, color: val }, index);
		}
	};
}

export class FormFieldEditorSelectList extends FormFieldEditor<FormModelFieldSelectList> {
	isAnalyst = Services.User.isIkeAnalyst(Services.User.current);

	override viewInfoRW() {
		const children: MithrilVNode[] = [this.viewOptionTitle()];

		for (let index = 0; index < this.field.options.length; index++) {
			children.push(this.viewOptionRow(index));
		}

		if (!this.field.isLocked || this.isAnalyst) {
			children.push(this.viewNewSelectListOption());
		}

		return m('div', { className: CSS.container }, children);
	}

	/**
	 * Only set when this field is the designated Type field of a span-typed subform.
	 * Ordinary select lists (even ones containing span type values) get no option protection.
	 */
	private get spanTypeOptionSet(): SpanTypeOptionSet | null {
		const parent = this.field.parent;
		if (!parent || parent.subFormType !== SubFormType.SPAN) {
			return null;
		}
		if (parent.subFormSpanTypeState?.typeFieldId !== this.field.id) {
			return null;
		}
		return SpanTypeOptionSet.forForm(this.field.getForm());
	}

	private viewOptionRow(index: number): MithrilVNode {
		const optionSet = this.spanTypeOptionSet;
		const optionValue = this.field.options[index]?.value;

		// Only the first occurrence of a protected value is the canonical role option;
		// a user-added duplicate stays editable/deletable so the unique-value error can be resolved
		const isFirstOccurrence = this.field.options.findIndex((opt) => opt.value === optionValue) === index;
		const isProtected = !!optionSet && !!optionValue && isFirstOccurrence && optionSet.isProtectedValue(optionValue);

		// Building is value-protected but may still be deleted
		const isDeletable = !isProtected || (!!optionValue && !!optionSet?.isDeletableProtectedValue(optionValue));

		const textState = this.getInputState('text', index);
		const valueState = this.getInputState('value', index, undefined, undefined, isProtected);
		const colorState = this.getInputState('color', index);

		const showDeleteButton = (!this.field.isLocked || this.isAnalyst) && isDeletable && index > 0;

		return m('div', { className: CSS.list }, [
			...this.viewSelectListOption(
				Input.view(textState),
				Input.view(valueState),
				this.isColorColumnVisible() ? Input.view(colorState, { rawInputOnly: true }) : null
			),
			showDeleteButton ? this.viewOptionDeleteButton(index) : null,
		]);
	}

	private viewOptionTitle(): MithrilVNode {
		const titleArr = [];

		if (this.isColorColumnVisible()) {
			titleArr.push(m('div', { className: classNames(CSS.title, CSS.colorTitle) }, Translate(T.COLOR)));
		}

		titleArr.push(
			m('div', { className: classNames(CSS.title, CSS.option) }, Translate(T.SELECT_LIST_TITLE)),
			m('div', { className: classNames(CSS.title, CSS.value) }, Translate(T.SELECT_LIST_VALUE))
		);

		return m('div', { className: CSS.list }, titleArr);
	}

	private viewSelectListOption(
		key: string | MithrilVNode,
		value: string | MithrilVNode,
		color?: string | MithrilVNode
	): MithrilVNode[] {
		return [
			this.isColorColumnVisible() ? m('div', { className: CSS.color }, color) : null,
			m('div', { className: CSS.option }, key),
			m('div', { className: CSS.value }, value),
		];
	}

	private viewNewSelectListOption(): MithrilVNode {
		const index = this.field.options.length;
		const textState = this.getInputState('text', index, `${Translate(T.SELECT_LIST_TITLE)} ${index}`);
		const defaultValue = `${Translate(T.SELECT_LIST_VALUE)} ${index}`;
		const valueState = this.getInputState('value', index, defaultValue);
		const colorState = this.getInputState('color', index, '', '#a0aec0');

		return m('div', { className: CSS.list }, [
			this.isColorColumnVisible()
				? m('div', { className: `${CSS.color} ${CSS.ghost}` }, Input.view(colorState, { rawInputOnly: true }))
				: null,
			m('div', { className: `${CSS.option} ${CSS.ghost}` }, Input.view(textState)),
			m('div', { className: `${CSS.value} ${CSS.ghost}` }, Input.view(valueState)),
		]);
	}

	private viewOptionDeleteButton(index: number): MithrilVNode {
		return ImageButton.view(MaterialIcon.DELETE, {
			onclick: () => {
				this.field.Mutator.remove(index);
				this.state.clearStates();
			},
		});
	}

	private getInputState(
		type: 'text' | 'value' | 'color',
		index: number,
		placeholder?: string,
		value?: string,
		readOnly?: boolean
	): InputState {
		const fieldKey = `${index}:${type}`;
		let editState = this.state.get(fieldKey);
		if (editState == null) {
			editState = this.state.child(T.EMPTY, fieldKey, type === 'color' ? InputStateTypes.COLOR : InputStateTypes.TEXT);
			editState.onValue(setterListValue(this.field, type, index));

			if (placeholder != null) {
				editState.setPlaceholder(placeholder);
			}

			if (value != null) {
				editState.setValue(value);
			} else if (index < this.field.options.length) {
				editState.setValue(this.field.options[index][type]);
			}
		}

		editState.setDisabled((index === 0 && type === 'value') || !!readOnly || (this.field.isLocked && !this.isAnalyst));

		editState.isRequired = type !== 'color' ? placeholder == null : false;

		if (type === 'value' && placeholder == null) {
			const currentValue = this.field.options[index]?.value;
			const isDuplicate =
				currentValue != null && this.field.options.some((opt, i) => i !== index && opt.value === currentValue);
			editState.setError(isDuplicate ? T.SELECT_LIST_VALUE_UNIQUE : null);
			if (isDuplicate) {
				editState.setTouched();
			}
		}

		return editState;
	}

	private isColorColumnVisible(): boolean {
		return Services.Token.has(IkeToken.FEATURE_COLOR_FLAG) && !!this.field.isMapColorField;
	}
}
